import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Undo2, FileText } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { formatMoeda } from '@fiodecobre/shared';
import { ModalResultadoDevolucao, type ResultadoDevolucao } from '../../components/notas-fiscais/ModalResultadoDevolucao';

// [DA-559] Devolver mercadoria AO FORNECEDOR — a unica devolucao da casa em que o estoque SAI.
// PARCIAL desde o inicio: o caso que originou a onda e 1 item de 3.
//
// O saldo NAO e calculado aqui. Ele vem de GET /api/nfe/devolucao-de-compra/:id/itens, e o backend
// refaz a mesma conta sob transacao antes de emitir — a tela e' previa, nunca autoridade (mesma
// decisao do saldo de remessa da [DA-372]).

interface ItemSaldo {
  id: number;
  numero_item: number | null;
  produto_id: number | null;
  codigo_fornecedor: string | null;
  descricao: string | null;
  ncm: string | null;
  unidade: string | null;
  quantidade: string;
  qtd_real: string | null;
  valor_unitario: string;
  valor_total: string;
  aliquota_icms: string;
  valor_icms: string;
  valor_difal: string;
  valor_ipi: string;
  valor_frete: string | null;
  valor_bc_icms: string | null;
  produto_codigo: string | null;
  produto_descricao: string | null;
  qtd_divergencia_aberta: string;
  qtd_ja_devolvida: string;
}

interface Props {
  notaId: number;
  numeroNota: string;
  fornecedorNome: string | null;
  onFechar: () => void;
  onEmitida: () => void;
}

function qtdBr(n: number): string {
  return Number(n).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
}

export function ModalDevolverAoFornecedor({ notaId, numeroNota, fornecedorNome, onFechar, onEmitida }: Props) {
  const toast = useToast();
  const [itens, setItens] = useState<ItemSaldo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [quantidades, setQuantidades] = useState<Record<number, number>>({});
  const [emitindo, setEmitindo] = useState(false);
  const [gerandoPrevia, setGerandoPrevia] = useState(false);
  const [resultado, setResultado] = useState<ResultadoDevolucao | null>(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const dados = await api.get<ItemSaldo[]>(`/api/nfe/devolucao-de-compra/${notaId}/itens`);
        if (!vivo) return;
        setItens(dados);
        // Nasce tudo em ZERO. O operador marca o que volta — o caso comum e 1 item de N, e
        // pre-marcar tudo convidaria a devolver a nota inteira por engano.
        setQuantidades({});
      } catch (err: any) {
        if (vivo) setErroCarga(err.message || 'Erro ao carregar itens da nota');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, [notaId]);

  function saldoDe(it: ItemSaldo): number {
    return Number(it.quantidade) - Number(it.qtd_ja_devolvida);
  }
  function bloqueadoPor(it: ItemSaldo): string | null {
    if (Number(it.qtd_divergencia_aberta) > 0) {
      return `${qtdBr(Number(it.qtd_divergencia_aberta))} já saiu do estoque como divergência de recebimento ("não chegou") — não dá pra devolver o que não chegou`;
    }
    if (saldoDe(it) <= 0) return 'já foi devolvido por inteiro';
    if (!it.ncm) return 'sem NCM na nota — a SEFAZ recusa';
    return null;
  }

  // Espelha a conta do backend ([DA-559] p.2 / regra W16-10 do MOC): o valor da nota e
  // produto + frete + IPI devolvido. A previa que mostra so o produto mente sobre o documento.
  const resumo = useMemo(() => {
    let produtos = 0;
    let frete = 0;
    let ipi = 0;
    let icms = 0;
    let difal = 0;
    const escolhidos: Array<{ item_id: number; quantidade: number }> = [];
    for (const it of itens) {
      const q = quantidades[it.id] ?? 0;
      if (!(q > 0)) continue;
      const qtdNota = Number(it.quantidade);
      const fracao = qtdNota > 0 ? q / qtdNota : 1;
      const v = Number(it.valor_unitario) * q;
      produtos += v;
      frete += it.valor_frete == null ? 0 : Number(it.valor_frete) * fracao;
      ipi += Number(it.valor_ipi ?? 0) * fracao;
      const bc = it.valor_bc_icms == null ? v : Number(it.valor_bc_icms) * fracao;
      icms += bc * Number(it.aliquota_icms) / 100;
      // O DIFAL nao vai pro XML — quem lanca e o contador. Fica na previa so pra ele saber a
      // ordem de grandeza do que sai da apuracao.
      difal += qtdNota > 0 ? Number(it.valor_difal) * fracao : 0;
      escolhidos.push({ item_id: it.id, quantidade: q });
    }
    return { produtos, frete, ipi, valor: produtos + frete + ipi, icms, difal, escolhidos };
  }, [itens, quantidades]);

  // Botao desabilitado DIZ por que — nunca cinza e mudo.
  const impedimento = useMemo((): string | null => {
    if (carregando) return 'Carregando os itens da nota...';
    if (resumo.escolhidos.length === 0) return 'Informe a quantidade de pelo menos um item pra devolver.';
    if (motivo.trim().length < 3) return 'Escreva o motivo da devolução (mínimo 3 caracteres) — ele vai nas informações complementares da nota.';
    return null;
  }, [carregando, resumo.escolhidos.length, motivo]);

  // Previa em PDF pro contador conferir antes de a nota existir. Nao consome numero de nota nem
  // fala com a SEFAZ — o mesmo XML da emissao, so que desenhado e carimbado.
  async function verPrevia() {
    if (resumo.escolhidos.length === 0) return;
    setGerandoPrevia(true);
    try {
      const blob = await api.postBlob(
        `/api/nfe/devolucao-de-compra/${notaId}/previa`,
        { itens: resumo.escolhidos, motivo: motivo.trim() || 'Simulacao' },
      );
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Solta o objeto depois que a aba pegou — revogar na hora deixa a aba em branco.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      toast.erro(err.message || 'Erro ao gerar a previa');
    } finally {
      setGerandoPrevia(false);
    }
  }

  async function emitir() {
    if (impedimento) return;
    setEmitindo(true);
    try {
      const r = await api.post<ResultadoDevolucao & { autorizada?: boolean; xMotivo?: string; cStat?: string | null }>(
        `/api/nfe/devolucao-de-compra/${notaId}`,
        { itens: resumo.escolhidos, motivo: motivo.trim() },
      );
      if (r.autorizada) {
        setResultado(r);
        onEmitida();
      } else {
        // Recusa da SEFAZ nao e' erro de sistema: a nota nao virou documento e nada saiu do
        // estoque. Mostra o cStat literal — e ele que diz o que consertar.
        toast.erro(`SEFAZ recusou (cStat ${r.cStat ?? '—'}): ${r.xMotivo ?? 'sem motivo'}`);
      }
    } catch (err: any) {
      toast.erro(err.message || 'Erro ao emitir a devolução');
    } finally {
      setEmitindo(false);
    }
  }

  if (resultado) {
    return (
      <ModalResultadoDevolucao
        resultado={resultado}
        origemRotulo="nota de entrada"
        origemNumero={numeroNota}
        sentido="saida"
        onFechar={() => { setResultado(null); onFechar(); }}
      />
    );
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={`Devolver ao fornecedor — NF ${numeroNota}${fornecedorNome ? ` · ${fornecedorNome}` : ''}`}
      // 1360: a coluna Devolver ganhou o link "devolver tudo (N)" e em 1100 ele quebrava em duas
      // linhas, desalinhando a coluna toda. Tamanho FIXO no codigo e a convencao da casa ([DA-047]).
      largura={1360}
      rodape={
        <div className="flex items-center justify-between w-full gap-4">
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            {resumo.escolhidos.length > 0 ? (
              <>
                <strong>Valor da nota: {formatMoeda(resumo.valor)}</strong> em {resumo.escolhidos.length}{' '}
                {resumo.escolhidos.length === 1 ? 'item' : 'itens'}
                {/* A composicao fica visivel: e ela que o contador conferiu, e um total sozinho
                    nao deixa ver se o frete ou o IPI entrou. So mostra as parcelas que existem. */}
                {(resumo.frete > 0.005 || resumo.ipi > 0.005) && (
                  <span className="muted">
                    {' '}({formatMoeda(resumo.produtos)} produtos
                    {resumo.ipi > 0.005 && ` + ${formatMoeda(resumo.ipi)} IPI`}
                    {resumo.frete > 0.005 && ` + ${formatMoeda(resumo.frete)} frete`})
                  </span>
                )}
                <span className="muted"> · ICMS destacado {formatMoeda(resumo.icms)}</span>
                {resumo.difal > 0.005 && <span className="muted"> · DIFAL proporcional {formatMoeda(resumo.difal)}</span>}
              </>
            ) : (
              <span className="muted">Nenhum item marcado.</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {impedimento && !carregando && (
              <span style={{ fontSize: 11, color: '#b45309', maxWidth: 360, textAlign: 'right', lineHeight: 1.35 }}>
                {impedimento}
              </span>
            )}
            <button type="button" onClick={onFechar} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
              Cancelar
            </button>
            {/* Fica ANTES de emitir, e habilita com menos exigencia (nao pede motivo): conferir e'
                o passo que vem antes, e obrigar a preencher tudo pra so entao poder olhar inverte
                a ordem. */}
            <button
              type="button"
              onClick={verPrevia}
              disabled={gerandoPrevia || resumo.escolhidos.length === 0}
              className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2 border"
              style={{ borderColor: 'var(--input-border)' }}
              title="Gera o PDF do que seria emitido, sem emitir nada — pra conferir com o contador"
            >
              <FileText size={14} />
              {gerandoPrevia ? 'Gerando...' : 'Ver prévia (PDF)'}
            </button>
            <button
              type="button"
              onClick={emitir}
              disabled={!!impedimento || emitindo}
              className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
              style={{ background: '#b45309', color: '#fff' }}
            >
              <Undo2 size={14} />
              {emitindo ? 'Emitindo na SEFAZ...' : 'Emitir devolução'}
            </button>
          </div>
        </div>
      }
    >
      {erroCarga ? (
        <div style={{ fontSize: 13, color: '#b91c1c' }}>{erroCarga}</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fde68a', color: '#000', borderRadius: 6, padding: 10, fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              Isto emite uma <strong>NF-e de saída</strong> na SEFAZ e <strong>tira a mercadoria do estoque</strong>.
              O ICMS da nota de entrada é destacado nos campos próprios (exigência do art. 59 § 9º da Res. CGSN 140/2018 —
              sem ele o fornecedor perde o crédito). A conta a pagar <strong>não é alterada</strong>: quem refaz o boleto é o fornecedor.
            </div>
          </div>

          <table className="w-full text-sm">
            <thead className="grid-subheader">
              <tr>
                <th className="text-center w-8">#</th>
                <th className="text-left w-20">Cód. Forn.</th>
                <th className="text-left">Produto / Descrição NF</th>
                <th className="text-center w-16">Emb.</th>
                <th className="text-right w-24">Qtd Nota</th>
                <th className="text-right w-36">Devolver</th>
                <th className="text-right w-28">Valor</th>
                <th className="text-right w-16">Alíq.</th>
                <th className="text-right w-28">ICMS</th>
              </tr>
            </thead>
            <tbody className="grid-body">
              {carregando && (
                <tr><td colSpan={9} className="px-2 py-4 text-center text-xs muted">Carregando...</td></tr>
              )}
              {!carregando && itens.map((it) => {
                const bloqueio = bloqueadoPor(it);
                const saldo = saldoDe(it);
                const q = quantidades[it.id] ?? 0;
                const marcado = q > 0;
                const unit = Number(it.valor_unitario);
                const aliq = Number(it.aliquota_icms);
                return (
                  <tr key={it.id} className="border-b" style={marcado ? { backgroundColor: '#fde68a', color: '#000' } : undefined}>
                    <td className="px-2 py-2 text-center text-xs muted">{it.numero_item ?? '—'}</td>
                    <td className="px-2 py-2 codigo-curto text-xs">{it.codigo_fornecedor || '—'}</td>
                    <td className="px-2 py-2 text-xs">
                      <div className="font-medium">{it.produto_descricao || '—'}</div>
                      <div className="text-xs muted truncate max-w-[300px]">{it.descricao || ''}</div>
                      {bloqueio && (
                        <div style={{ fontSize: 10, color: '#b45309', fontWeight: 600, lineHeight: 1.3, marginTop: 2 }}>
                          Não dá pra devolver: {bloqueio}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center text-xs">{it.unidade || '—'}</td>
                    <td className="px-2 py-2 text-right text-xs">
                      {qtdBr(Number(it.quantidade))}
                      {/* Sem esta linha o operador digita "1200" achando que o campo espera metros,
                          e a nota sai com 1200 CDs. E o mesmo erro de ancora que quebrou a
                          Divergencia de Recebimento — aqui a unidade fica escrita. */}
                      {it.qtd_real != null && Number(it.qtd_real) !== Number(it.quantidade) && (
                        <div className="text-[10px] muted leading-tight">
                          = {qtdBr(Number(it.qtd_real))} no estoque
                        </div>
                      )}
                      {Number(it.qtd_ja_devolvida) > 0 && (
                        <div className="text-[10px] leading-tight" style={{ color: '#b45309' }}>
                          {qtdBr(Number(it.qtd_ja_devolvida))} já devolvido
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {/* ⛔ `step` NAO pode ser 0.0001 aqui. Herdei isso do modal de divergencia e
                          em 17/08/2026 custou quase uma nota errada: UM clique na setinha do campo
                          vazio poe 0,0001, o rodape passa a dizer "R$ 0,24" no lugar de R$ 2.379,39,
                          e a tela fica valida e pronta pra emitir. A quantidade aqui esta na
                          unidade da NOTA (1 CD, 2 CD) — a setinha tem que andar de 1 em 1, e so
                          aceita fracao quando o proprio item vem fracionado (kg, metro). */}
                      <input
                        type="number"
                        step={Number.isInteger(saldo) ? 1 : 'any'}
                        min="0"
                        max={saldo}
                        disabled={!!bloqueio}
                        value={q === 0 ? '' : q}
                        placeholder="0"
                        onChange={(e) => {
                          const v = Math.min(Math.max(0, Number(e.target.value)), saldo);
                          setQuantidades((prev) => ({ ...prev, [it.id]: v }));
                        }}
                        className="w-20 text-right text-xs border rounded px-2 py-1 disabled:bg-gray-50"
                        style={{ borderColor: 'var(--input-border)' }}
                      />
                      {/* Caso comum desta loja: o item volta INTEIRO. Um clique aqui evita digitar
                          numero e mata a duvida de unidade (1 CD? 1.200 m?) que o campo carrega. */}
                      {!bloqueio && q !== saldo && (
                        <button
                          type="button"
                          onClick={() => setQuantidades((prev) => ({ ...prev, [it.id]: saldo }))}
                          className="block ml-auto mt-1 text-[10px] underline"
                          // nowrap: o link e' curto e quebrar "devolver tudo / (1)" em duas linhas
                          // desalinha a coluna inteira. A largura do modal e da coluna acompanham.
                          style={{ color: 'var(--primary)', background: 'none', border: 0, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' }}
                        >
                          devolver tudo ({qtdBr(saldo)})
                        </button>
                      )}
                      {/* Fracao onde a nota so tem inteiro = quase sempre setinha clicada por
                          engano. O rodape ja mostrava "R$ 0,24" e passou batido — valor pequeno
                          nao chama atencao sozinho; o aviso tem que estar NA LINHA que causou. */}
                      {q > 0 && Number.isInteger(saldo) && !Number.isInteger(q) && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', lineHeight: 1.3, marginTop: 2 }}>
                          ⚠️ fração de {qtdBr(saldo)} {it.unidade || 'emb.'} — confira
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-xs font-medium">
                      {marcado ? formatMoeda(unit * q) : <span className="muted">—</span>}
                    </td>
                    <td className="px-2 py-2 text-right text-xs">{aliq > 0 ? `${aliq.toFixed(2)}%` : '—'}</td>
                    <td className="px-2 py-2 text-right text-xs font-medium">
                      {marcado && aliq > 0 ? formatMoeda(unit * q * aliq / 100) : <span className="muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* O motivo vem DEPOIS dos itens (pedido dele, 17/08): a ordem da tela passou a ser a
              ordem da decisao — primeiro escolher o que volta, depois dizer por que. Antes ele
              estava no topo e pedia a justificativa de algo que ainda nao tinha sido escolhido. */}
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 3 }}>
              Motivo da devolução
            </label>
            <input
              type="text"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={200}
              placeholder="Ex.: mercadoria divergente do pedido"
              className="w-full text-sm border rounded px-2 py-1.5"
              style={{ borderColor: 'var(--input-border)' }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
              Vai nas informações complementares da nota — é o que o fornecedor lê. Não dá pra editar depois de autorizada.
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
